/**
 * Real driving distance + time via Google's Distance Matrix (ROUTE-REAL-01).
 *
 * COST DISCIPLINE: Distance Matrix is a metered API ($ per element). To keep it
 * cheap we (1) only ever request CONSECUTIVE legs of a route the dispatcher is
 * actually viewing — N-1 one-element calls, never an N×N matrix; (2) cache every
 * leg by its rounded coordinates for the whole session, so re-viewing / re-
 * ordering a route only pays for the legs that changed; (3) stop calling entirely
 * if the API reports it isn't enabled (sticky `apiUnavailable`). Callers fall back
 * to the straight-line estimate (route.ts) whenever this returns null.
 *
 * Requires the "Distance Matrix API" to be enabled on the Maps key in Google
 * Cloud. Until it is, every call returns null and the UI shows the estimate.
 */
import { useEffect, useState } from "react";
import type { LatLng } from "./route";
import { useGoogleMaps } from "./useGoogleMaps";

const cache = new Map<string, { miles: number; minutes: number }>();
let apiUnavailable = false;

function legKey(a: LatLng, b: LatLng): string {
  return `${a.lat.toFixed(4)},${a.lng.toFixed(4)}>${b.lat.toFixed(4)},${b.lng.toFixed(4)}`;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function service(): any | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const g = (window as any).google;
  if (!g?.maps?.DistanceMatrixService) return null;
  return new g.maps.DistanceMatrixService();
}

async function getLeg(
  a: LatLng,
  b: LatLng,
): Promise<{ miles: number; minutes: number } | null> {
  const k = legKey(a, b);
  const hit = cache.get(k);
  if (hit) return hit;
  if (apiUnavailable) return null;
  const svc = service();
  if (!svc) return null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const g = (window as any).google;
  return new Promise((resolve) => {
    svc.getDistanceMatrix(
      {
        origins: [{ lat: a.lat, lng: a.lng }],
        destinations: [{ lat: b.lat, lng: b.lng }],
        travelMode: g.maps.TravelMode.DRIVING,
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (res: any, status: string) => {
        if (status !== "OK") {
          // REQUEST_DENIED / OVER_QUERY_LIMIT → the API isn't enabled / is capped.
          // Stop hammering it; the caller falls back to the estimate.
          if (status === "REQUEST_DENIED" || status === "OVER_QUERY_LIMIT")
            apiUnavailable = true;
          resolve(null);
          return;
        }
        const el = res?.rows?.[0]?.elements?.[0];
        if (!el || el.status !== "OK") {
          resolve(null);
          return;
        }
        const out = {
          miles: el.distance.value / 1609.34,
          minutes: el.duration.value / 60,
        };
        cache.set(k, out);
        resolve(out);
      },
    );
  });
}

export interface RouteDrive {
  totalMiles: number;
  totalMinutes: number;
  complete: boolean; // every leg resolved (vs some legs missing)
}

/** Real drive distance + time along an ORDERED route. null = API unavailable. */
export async function getRouteDrive(
  ordered: LatLng[],
): Promise<RouteDrive | null> {
  if (ordered.length < 2)
    return { totalMiles: 0, totalMinutes: 0, complete: true };
  const legs: [LatLng, LatLng][] = [];
  for (let i = 0; i + 1 < ordered.length; i++)
    legs.push([ordered[i], ordered[i + 1]]);
  const results = await Promise.all(legs.map(([a, b]) => getLeg(a, b)));
  if (results.every((r) => r === null)) return null; // API unavailable
  let totalMiles = 0;
  let totalMinutes = 0;
  let complete = true;
  for (const r of results) {
    if (r) {
      totalMiles += r.miles;
      totalMinutes += r.minutes;
    } else complete = false;
  }
  return { totalMiles, totalMinutes, complete };
}

/** Hook: real drive for an ordered route, refetched only when the order changes. */
export function useRouteDrive(ordered: LatLng[]): {
  data: RouteDrive | null;
  loading: boolean;
} {
  // Wait for the Maps SDK — without this gate the first fetch would race the
  // SDK load, silently get nothing, and never retry (the sig doesn't change).
  const { ready } = useGoogleMaps();
  const sig = ordered
    .map((p) => `${p.lat.toFixed(4)},${p.lng.toFixed(4)}`)
    .join("|");
  const [state, setState] = useState<{
    data: RouteDrive | null;
    loading: boolean;
  }>({ data: null, loading: false });

  useEffect(() => {
    let cancelled = false;
    if (!ready) return;
    if (ordered.length < 2) {
      setState({
        data: { totalMiles: 0, totalMinutes: 0, complete: true },
        loading: false,
      });
      return;
    }
    setState((s) => ({ ...s, loading: true }));
    getRouteDrive(ordered).then((d) => {
      if (!cancelled) setState({ data: d, loading: false });
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig, ready]);

  return state;
}
